import { Record1099NEC } from "./interface/1099-NEC-interface";
import { businessNameControl, personNameControl } from "../../constants/recipientIdentity";

function isBusinessRecipient(record: Record1099NEC): boolean {
  // Legacy TSC1099 stores RECIPIENT_ID_TYPE as char(1): '1'=EIN, '2'=SSN/ITIN/ATIN,
  // '3'=UNKNOWN; the portal upload layout may carry the spelled-out form.
  const idType = (record.RECIPIENT_ID_TYPE || "").toString().trim().toUpperCase();
  if (
    idType === "1" ||
    idType === "E" ||
    idType === "EIN" ||
    idType.includes("BUSINESS")
  ) {
    return true;
  }
  if (record.RECIPIENT_BUSINESS_NAME1?.trim()) {
    return true;
  }
  return false;
}

function usesSplitNameLayout(record: Record1099NEC): boolean {
  return !!(
    record.RECIPIENT_ID_TYPE ||
    record.RECIPIENT_FIRST_NAME ||
    record.RECIPIENT_BUSINESS_NAME1
  );
}

export function buildRecipientDetail(
  record: Record1099NEC,
  options: { maskTin: boolean }
) {
  const tin = options.maskTin
    ? "000000000"
    : (record.RECIPIENT_ID_NUMBER || "").replace(/-/g, "");

  if (usesSplitNameLayout(record) && isBusinessRecipient(record)) {
    const businessName = record.RECIPIENT_BUSINESS_NAME1 || "UNKNOWN";
    const control = businessNameControl(businessName);
    return {
      TIN: tin,
      TINSubmittedTypeCd: "BUSINESS_TIN",
      ...(control && { BusinessNameControlTxt: control }),
      BusinessName: {
        BusinessNameLine1Txt: businessName,
        ...(record.RECIPIENT_BUSINESS_NAME2 && {
          BusinessNameLine2Txt: record.RECIPIENT_BUSINESS_NAME2,
        }),
      },
      MailingAddressGrp: buildMailingAddress(record),
    };
  }

  if (usesSplitNameLayout(record)) {
    const first = record.RECIPIENT_FIRST_NAME || "UNKNOWN";
    const last = record.RECIPIENT_LAST_NAME || "UNKNOWN";
    const control = personNameControl(record, first, last);
    return {
      TIN: tin,
      TINSubmittedTypeCd: "INDIVIDUAL_TIN",
      ...(control && { PersonNameControlTxt: control }),
      PersonName: {
        PersonFirstNm: first,
        ...(record.RECIPIENT_MIDDLE_NAME && {
          PersonMiddleNm: record.RECIPIENT_MIDDLE_NAME,
        }),
        PersonLastNm: last,
        ...(record.RECIPIENT_SUFFIX && { SuffixNm: record.RECIPIENT_SUFFIX }),
      },
      MailingAddressGrp: buildMailingAddress(record),
    };
  }

  const legacyFirst = record.RECIPIENT_NAME || "UNKNOWN";
  const legacyLast = record.RECIPIENT_NAME2 || "UNKNOWN";
  const control = legacyFirst.substring(0, 4).toUpperCase();
  return {
    TIN: tin,
    TINSubmittedTypeCd: "INDIVIDUAL_TIN",
    ...(control && { PersonNameControlTxt: control }),
    PersonName: {
      PersonFirstNm: legacyFirst,
      ...(legacyLast && { PersonLastNm: legacyLast }),
    },
    MailingAddressGrp: buildMailingAddress(record),
  };
}

function buildMailingAddress(record: Record1099NEC) {
  return {
    USAddress: {
      AddressLine1Txt: record.RECIPIENT_ADDRESS1 || "UNKNOWN ADDRESS",
      ...(record.RECIPIENT_ADDRESS2 && {
        AddressLine2Txt: record.RECIPIENT_ADDRESS2,
      }),
      CityNm: record.RECIPIENT_CITY || "UNKNOWN CITY",
      StateAbbreviationCd: (record.RECIPIENT_STATE || "IL").substring(0, 2),
      ZIPCd: record.RECIPIENT_ZIP || "00000",
    },
  };
}

export function directSaleAboveThreshold(record: Record1099NEC): "0" | "1" {
  const flag =
    record.BOX2_DIRECT_SALES === "X" || record.BOX2_DIRECT_SALE === "X";
  return flag ? "1" : "0";
}

export function buildPrevSubmittedPersonName(record: Record1099NEC) {
  if (usesSplitNameLayout(record) && !isBusinessRecipient(record)) {
    return {
      PersonFirstNm: record.RECIPIENT_FIRST_NAME || "UNKNOWN",
      ...(record.RECIPIENT_MIDDLE_NAME && {
        PersonMiddleNm: record.RECIPIENT_MIDDLE_NAME,
      }),
      PersonLastNm: record.RECIPIENT_LAST_NAME || "UNKNOWN",
      ...(record.RECIPIENT_SUFFIX && { SuffixNm: record.RECIPIENT_SUFFIX }),
    };
  }
  return {
    PersonFirstNm: record.RECIPIENT_NAME || "UNKNOWN",
    PersonLastNm: record.RECIPIENT_NAME2 || "UNKNOWN",
  };
}

// Year resolution now lives in the shared module so ALL forms use the same rules;
// re-exported here to keep existing imports working.
export {
  IRS_MAX_INTAKE_TAX_YEAR,
  resolveTaxYear,
  resolveIrsIntakeTaxYear,
} from "../../constants/taxYear";
